import { PermissionsServiceTokens as Tokens } from '../di-tokens';
import type { Permission } from '../domain';
import type { UserWithPermissionsProvider } from './ports';

/**
 * Check if current user has a permission.
 *
 * @example
 * import { makeCurrentUserPermissionChecker } from '..';
 * const makePermissionChecker = injector.injectFunction(makeCurrentUserPermissionChecker);
 * const checker = await makePermissionChecker();
 * const result = checker.hasPermission(Permissions.AdminApi);
 */
export function makeCurrentUserPermissionChecker(userProfileProvider: UserWithPermissionsProvider) {
    return async () => {
        const user = await userProfileProvider.getCurrentUser();
        return {
            hasPermission(permission: Permission) {
                return user.hasPermission(permission);
            },

            hasAnyPermission(...permissions: Permission[]) {
                return permissions.some(permission => user.hasPermission(permission));
            },

            hasEveryPermission(...permissions: Permission[]) {
                return permissions.every(permission => user.hasPermission(permission));
            },
        };
    };
}

export type PermissionChecker = Awaited<ReturnType<ReturnType<typeof makeCurrentUserPermissionChecker>>>;

makeCurrentUserPermissionChecker.inject = [Tokens.UserProfileProvider] as const;
